import { LocationStrategy } from '@angular/common';
import { Injectable } from '@angular/core';
import * as URI from 'urijs';

import { AppConfig } from '®shared/config';

import { GoogleAnalyticsCustomDimensions } from '../ga-dimensions';

import { GoogleAnalyticsEventNames } from './google-analytics-event-names';
import * as Gtag from './gtag';

const VERSION = 'v2';

declare const gtag: Gtag.gtag;

@Injectable()
export class GoogleAnalyticsService {
  private isEmbeddedMode: boolean;
  private tenantCode: string;
  private appBaseHref: string;
  private tenantHttpsBaseUri: string;

  constructor(appConfig: AppConfig, locationStrategy: LocationStrategy) {
    this.isEmbeddedMode = appConfig.ltiLaunchId === null ? false : true;
    this.tenantCode = appConfig.tenant.shortCode;
    this.tenantHttpsBaseUri = appConfig.tenant.httpsBaseUri;
    this.appBaseHref = locationStrategy.getBaseHref();
  }

  /**
   * Send a `page_view` to Google Analytics.
   *
   * @param {string} pagePath - a fake page path to include in the pageview
   * @param {string} pageName - a fake pageName dimension value to include in the pageview
   * @param {boolean} isLoggedIn - `true` for a logged in user; `false` otherwise
   */
  sendPageView(pagePath: string, pageName: string, isLoggedIn: boolean): void {
    const { isEmbeddedMode, tenantShortCode, version } =
      this.defaultGoogleAnalyticsDimensions();

    const cleanedPath = this.cleanPath(pagePath);
    const dimensions = {
      isEmbeddedMode,
      isLoggedIn,
      pageName,
      tenantShortCode,
      version,
    };

    const eventParams = {
      page_path: cleanedPath,
      ...dimensions,
    };

    this.sendEvent(GoogleAnalyticsEventNames.PAGE_VIEW, eventParams);
  }

  /**
   * Send an event to Google Analytics.
   *
   * @param {GoogleAnalyticsEventNames} eventName
   * @param {object} eventParams
   */
  sendEvent(eventName: GoogleAnalyticsEventNames, eventParams: object) {
    if (typeof gtag !== 'undefined' && gtag) {
      gtag('event', eventName, eventParams);
    }
  }

  /**
   * Get default values for GA custom dimensions, based on config
   *
   * @return {GoogleAnalyticsCustomDimensions} - custom dimension data from config
   */
  private defaultGoogleAnalyticsDimensions(): GoogleAnalyticsCustomDimensions {
    const tenantShortCode = this.tenantCode;
    const isEmbeddedMode = this.isEmbeddedMode;
    return { isEmbeddedMode, tenantShortCode, version: VERSION };
  }

  /**
   * Clean a page path to remove version and tenant code.
   *
   * @param {string} path - The raw path, to clean.
   * @return {string} The clean path
   */
  private cleanPath(path: string): string {
    if (!path) {
      return path;
    }

    const addIsEmbeddedFlag = (inputPath: string) =>
      new URI(inputPath).addQuery({ embed: true }).valueOf();

    const optionallyAddIsEmbedded = (inputPath: string) =>
      this.isEmbeddedMode ? addIsEmbeddedFlag(inputPath) : inputPath;

    if (path.startsWith(this.tenantHttpsBaseUri)) {
      // classic url
      return optionallyAddIsEmbedded(path.replace(this.tenantHttpsBaseUri, ''));
    } else {
      const pathWithoutBaseHref =
        this.appBaseHref && this.appBaseHref !== '/'
          ? path.replace(this.appBaseHref, '')
          : path;
      return optionallyAddIsEmbedded(
        pathWithoutBaseHref.replace(`\/3\/${this.tenantCode}\/`, '/'),
      );
    }
  }
}
